import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ShareIcon,
  EyeIcon,
  ClockIcon,
  TrashIcon,
  LinkIcon,
  UserIcon,
  CalendarIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

import { shareService } from '../../services/sharing';
import { format } from 'date-fns';

const ShareManagement = () => {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedShare, setSelectedShare] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchShares();
  }, []);

  const fetchShares = async () => {
    try {
      const response = await shareService.getUserShares();
      setShares(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load shares');
      setLoading(false);
    }
  };

  const handleRevokeShare = async (token) => {
    if (!window.confirm('Are you sure you want to revoke this share? The link will no longer work.')) {
      return;
    }

    try {
      await shareService.revokeShareToken(token);
      toast.success('Share revoked successfully');
      fetchShares();
    } catch (error) {
      toast.error('Failed to revoke share');
    }
  };

  const copyShareLink = (token) => {
    const shareUrl = `${window.location.origin}/shared/${token}`;
    navigator.clipboard.writeText(shareUrl);
    toast.success('Share link copied to clipboard');
  };

  const getStatusColor = (share) => {
    if (share.revoked) return 'bg-red-100 text-red-700';
    if (new Date(share.expiresAt) < new Date()) return 'bg-gray-100 text-gray-700';
    return 'bg-green-100 text-green-700';
  };

  const getStatusText = (share) => {
    if (share.revoked) return 'Revoked';
    if (new Date(share.expiresAt) < new Date()) return 'Expired';
    return 'Active';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-paper-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            className="flex items-center justify-between"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div>
              <h1 className="text-4xl font-serif font-bold mb-2">Share Management</h1>
              <p className="text-blue-100 text-lg">
                Manage your shared diary entries and access
              </p>
            </div>
            <motion.button
              onClick={() => setShowCreateModal(true)}
              className="bg-white text-blue-600 px-6 py-3 rounded-full font-medium hover:bg-blue-50 transition-colors flex items-center space-x-2"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <ShareIcon className="h-5 w-5" />
              <span>Create Share</span>
            </motion.button>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {shares.length > 0 ? (
          <motion.div
            className="space-y-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {shares.map((share, index) => (
              <motion.div
                key={share._id}
                className="card p-6 hover:shadow-lg transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <Link
                        to={`/entries/${share.entryId._id}`}
                        className="text-xl font-serif font-semibold text-ink-black hover:text-primary-600 transition-colors"
                      >
                        {share.entryId.title}
                      </Link>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(share)}`}>
                        {getStatusText(share)}
                      </span>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-gray-600">
                      <div className="flex items-center space-x-2">
                        <CalendarIcon className="h-4 w-4" />
                        <span>Created: {format(new Date(share.createdAt), 'MMM d, yyyy')}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <ClockIcon className="h-4 w-4" />
                        <span>Expires: {format(new Date(share.expiresAt), 'MMM d, yyyy')}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <EyeIcon className="h-4 w-4" />
                        <span>{share.accessCount} views</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <UserIcon className="h-4 w-4" />
                        <span>{share.permission === 'view' ? 'View only' : 'Can edit'}</span>
                      </div>
                    </div>

                    {share.sharedWithEmail && (
                      <div className="mt-2 text-sm text-gray-600">
                        Shared with: <span className="font-medium">{share.sharedWithEmail}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    <motion.button
                      onClick={() => copyShareLink(share.accessToken)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      title="Copy share link"
                    >
                      <LinkIcon className="h-5 w-5" />
                    </motion.button>

                    <Link
                      to={`/shares/${share.entryId._id}/analytics`}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      title="View analytics"
                    >
                      <ChartBarIcon className="h-5 w-5" />
                    </Link>

                    {!share.revoked && (
                      <motion.button
                        onClick={() => handleRevokeShare(share.accessToken)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        title="Revoke access"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </motion.button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div
            className="card p-12 text-center"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <ShareIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              No shared entries yet
            </h3>
            <p className="text-gray-500 mb-6">
              Start sharing your diary entries with friends and family
            </p>
            <motion.button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Create First Share
            </motion.button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ShareManagement;
